/* eslint-disable */
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import StoreFunctions from '../../../stores/storeFunctions'
import { CurrencyInfo } from 'types/interfaces'

const storeFunctions = new StoreFunctions()

interface CollectionProps {
  currencyInfo: CurrencyInfo
  shape: string | null
  productIds: string[]
  selectedDiamondSKU: string
  onError?: () => void
}

export const Collection: React.FC<CollectionProps> = props => {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [productTalesInfo, setProductTalesInfo] = useState<Array<any> | null>(null)

  const productIds = props.productIds || []

  const makeProductImage = (product: { [key: string]: any }, shape: string) => {
    let productImageInfo = {
      src: product.product_data.variants[0].image.original_src,
      alt: product.product_data.variants[0].image.alt_text
    }
    let found = false
    if (shape) {
      product.product_data.variants.forEach((variant: { [key: string]: any }) => {
        const variantShape = variant.selected_options.filter(
          (option: { [key: string]: any }) => option.name == 'Shape'
        )
        if (
          variantShape.length &&
          variantShape[0].value.toLowerCase() == shape &&
          !found &&
          typeof variant.image.original_src != 'undefined'
        ) {
          found = true
          productImageInfo = {
            src: variant.image.original_src,
            alt: variant.image.alt_text + ' ' + shape
          }
        }
      })
      if (!found && product.product_data.featuredImage != null) {
        found = true
        productImageInfo = {
          src: product.product_data.featuredImage.url,
          alt: product.product_data.featuredImage.altText
        }
      }
    }
    if (shape && !found) {
      productImageInfo = {
        src: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgc3R5bGU9ImZpbGw6dHJhbnNwYXJlbnQiIC8+Cjwvc3ZnPgo=',
        alt: ''
      }
    }
    return productImageInfo
  }

  const makeProductLink = (product: { [key: string]: any }, shape: string) => {
    let variantPart = '?diamond_selected=' + props.selectedDiamondSKU
    if (shape) {
      let found = false
      let variantId = null
      product.product_data.variants.forEach((variant: { [key: string]: any }) => {
        const variantShape = variant.selected_options.filter(
          (option: { [key: string]: any }) => option.name == 'Shape'
        )
        if (variantShape.length && variantShape[0].value.toLowerCase() == shape && !found) {
          found = true
          variantId = variant.variant_id
        }
      })

      if (variantId !== null) {
        variantPart = '?variant=' + variantId + '&diamond_selected=' + props.selectedDiamondSKU
      }
    }
    return '/products/' + product.product_data.handle + variantPart
  }

  const buildTales = (products: { [key: string]: any }[], shape: string, currencyRate: number) => {
    const tales: any[] = []
    products.forEach(product => {
      const productData = product?.settings?.product_data
      // Skip products whose Shopify product data failed to load (missing/partial),
      // otherwise dereferencing title/variants throws and breaks the whole page.
      if (!shape || !productData || !productData.variants?.length) {
        return
      }
      tales.push({
        id: product.settings.external_product_id,
        product_image: makeProductImage(product.settings, shape),
        product_link: makeProductLink(product.settings, shape),
        title: productData.title,
        price: storeFunctions.formatPrice(productData.variants[0].price * currencyRate, props.currencyInfo)
      })
    })
    return tales
  }

  const loadProducts = async () => {
    const shape = props.shape || ''
    const currencyRate = window.Shopify?.currency?.rate || 1
    if (!productIds.length) {
      setProductTalesInfo([])
      setLoaded(true)
      return
    }
    setLoaded(false)
    const products = await storeFunctions.getProductsForSKU(productIds)
    // null means the fetch failed (vs. an empty array, which is a valid "no
    // products" result). Surface the error instead of a silent blank grid.
    if (products === null) {
      props.onError?.()
      setProductTalesInfo([])
      setLoaded(true)
      return
    }
    const tales = buildTales(products, shape, currencyRate)
    // We only reach here with a non-empty productIds list, so an empty result
    // means every product was skipped for missing product_data/variants. Surface
    // the error modal instead of leaving shoppers on a silent blank grid.
    if (!tales.length) {
      props.onError?.()
      setProductTalesInfo([])
      setLoaded(true)
      return
    }
    setProductTalesInfo(tales)
    setLoaded(true)
  }

  useEffect(() => {
    if (productIds.length) {
      loadProducts()
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.productIds])

  const skeletonCount = 3

  return (
    <div className="sp-wrapper unbrid-collection-wrapper">
      <div className="unbrid-container">
        <div className="unbrid-row">
          <div className="unbridaled-single-page-diamond-info single-page-diamond-info">
            <h1 className="ub-hidden ila-hidden">{t('collection.pageHeading')}</h1>
          </div>
        </div>
        <div className="unbrid-row">
          {productTalesInfo && loaded
            ? productTalesInfo.map(item => <ProductTale key={item.id} p={item} />)
            : Array.from({ length: skeletonCount }).map((_, i) => <ProductTalesSkeleton key={i} />)}
        </div>
      </div>
    </div>
  )
}

const ProductTalesSkeleton = () => {
  return (
    <div className="unbrid-col unbrid-col-4">
      <div className="unbrid-product-wrapper">
        <div className="unbrid-product-image-wrapper">
          <Skeleton style={{ aspectRatio: 1 }} />
        </div>
        <div className="unbrid-product-title-wrapper">
          <Skeleton width={200} height={20} containerClassName="diamond-skeleton-title" />
        </div>
        <div className="unbrid-product-price-wrapper">
          <Skeleton width={50} height={16} containerClassName="diamond-skeleton-price" />
        </div>
      </div>
    </div>
  )
}

interface ProductTaleProps {
  p: { [key: string]: any }
}

const ProductTale: React.FC<ProductTaleProps> = props => {
  const { t } = useTranslation()
  const formattedPrice = t('collection.fromPrice', { price: props.p.price })
  return (
    <div className="unbrid-col unbrid-col-4">
      <div className="unbrid-product-wrapper">
        <div className="unbrid-product-image-wrapper react-loading-skeleton">
          <a href={props.p.product_link}>
            <img
              src={props.p.product_image.src}
              alt={props.p.product_image.alt}
              className="loading"
              onLoad={(e: React.MouseEvent<HTMLImageElement>) => {
                const target = e.target as HTMLImageElement
                target.classList.add('loaded')
                target
                  .closest('.unbrid-product-image-wrapper')
                  ?.classList.remove('react-loading-skeleton')
              }}
            />
          </a>
        </div>
        <div className="unbrid-product-title-wrapper">
          <a href={props.p.product_link}>
            <h3>{props.p.title}</h3>
          </a>
        </div>
        <div className="unbrid-product-price-wrapper">
          <a href={props.p.product_link} dangerouslySetInnerHTML={{ __html: formattedPrice }}></a>
        </div>
      </div>
    </div>
  )
}

export default Collection
